import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import { CHART_TYPE, DisplayInvalidDataPointAs } from '@tencent/chart/lib/chart-ui/interface';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import { STGrouping } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import {
    DEFAULT_SYMBOL,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/marker';
import { labelPositionTypeMap } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/map';
import { isEmptyValue } from '../utils';
import { ChartUILabelPosition, CustomStyles, ShapeOriginStyle } from '../interface/chart-ui.interface';
import type {
    IChartManualLayout,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type {
    ILineChartSeries,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/line-chart.interface';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type { ShapeStyle, ChartUIDataItem } from '../interface/chart-ui.interface';
import type { IGeometryOptions, IChartSeriesColor, IChartAdjust, IChartRendererConfig } from '@tencent/chart/lib/chart-ui/interface';
import type { Marker } from '@tencent/chart/lib/antv/g2/interface';
import type {
    ILineChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/line-chart.interface';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import {
    applyBgToLineStroke, createBgByLineStroke, filterOriginStyle, generateCustomStyles,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';
import { shallowMerge } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/merge';

export class LineChartConfigHelper extends BaseChartConfigHelper {
    public get type() {
        return CHART_TYPE.LINE_CHART;
    }

    public getRenderConfig(): IChartRendererConfig {
        const baseConfig = super.getRenderConfig();

        return {
            ...baseConfig,
            displayInvalidDataPointAs: DisplayInvalidDataPointAs.Gap,
            showSinglePoint: false,
            lineChartConfig: {
                pointVisible: this.pointVisible,
            },
        };
    }

    private get is3D(): boolean {
        return this.subChart.is3D;
    }

    private get pointVisible() {
        return this.isShowMarker(this.subChart.data);
    }

    protected get subChart(): ILineChartProperty {
        return this.context.getSubChartModel().getSubChartProperty() as ILineChartProperty;
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new LineChartDataLabel(label, this.context);
    }

    // 折线图底层是Line和point的组合图，两种图形共用一组数据源
    protected getGeometryOptions(): IGeometryOptions {
        return {
            geometryStyles: this.getGeometryStyles(),
            geometrySizes: this.getGeometrySizes(),
            geometryShapes: this.getGeometryShapes(),
        };
    }
    protected getGeometryStyle(chartDataItem: ChartUIDataItem): ShapeStyle {
        // g2目前做不到折线图分段，g2折线图每个系列只会触发一次getGeometryStyle，获取的是每个系列第一个数据的样式，作为整个折线的样式
        // office的折线是分段的，每一段的样式使用第二个端点的样式作为整条折线的样式
        // 比如：当第一个point是"无线条"，第二个点"有线条"的时候，使用第一个点的样式表现会是整条折线不显示，而office是使用的第二个点的样式
        // 所以这里取样式的时候，改成取第下一个点的样式。如果没有就使用这个点的样式（原则上不会出现这种情况）
        const dataItem = this.getNextDataItem(chartDataItem) || chartDataItem;
        const { border, fill } = dataItem || {};

        const lineWidth = border?.lineWidth;
        const lineDash = border?.lineDash;
        const lineStroke = this.is3D ? fill : border?.stroke;

        return {
            // 最终还是没有border数据的话，就取主题中的宽度
            lineWidth,
            stroke: lineStroke,
            lineDash,
            lineJoin: border?.lineJoin,
            lineCap: border?.lineCap,
            customStyles: this.getGeometryCustomStyles(chartDataItem),
        };
    }

    protected isLine(): boolean {
        return true;
    }

    protected getGeometryCustomStyles(chartDataItem: ChartUIDataItem): CustomStyles {
        const dataItem = this.getNextDataItem(chartDataItem) || chartDataItem;
        const { outline, background, ...rest } = filterOriginStyle(dataItem);
        const newOutline = { ...outline };  // 拷贝一份，防止原始数据被修改
        if (this.is3D) {
            applyBgToLineStroke(newOutline, background);
        }
        return generateCustomStyles({ outline: newOutline, ...rest });
    }

    protected getSeriesColor(): IChartSeriesColor | undefined {
        return this.series.map(ser => ser.border?.stroke || '');
    }

    protected getGeometryShape = (name: string) => {
        const chartSer = this.subChart.series.find(item => item.name === name);

        return (chartSer as ILineChartSeries)?.smooth ? 'smooth' : 'line';
    };

    protected getAdjustsConfig(): IChartAdjust[] {
        const adjusts = super.getAdjustsConfig();

        const { grouping } = this.subChart;

        if (grouping === STGrouping.stacked) {
            adjusts.push({
                type: 'stack',
                reverseOrder: false,
                stackPositiveAndNegativeSeparately: false,
            });
        }

        if (grouping === STGrouping.percentStacked) {
            // TODO 折线图里面isPercent字段无效，这个待研究，或许支持不了
            adjusts.push({
                type: 'stack',
                reverseOrder: false,
                isPercent: true,
                stackPositiveAndNegativeSeparately: false,
            });
        }

        return adjusts;
    }


    private getNextDataItem(chartDataItem: ChartUIDataItem) {
        return this.data.find(item => item.serIndex === chartDataItem.serIndex
            && item.categoryIndex === chartDataItem.categoryIndex + 1);
    }


    private getGeometryStyles() {
        return {
            point: this.createIdMappingCfg<ShapeStyle>((chartDataItem) => {
                const { marker, value, border, fill } = chartDataItem;
                let shapeStyle;
                if (!marker || isEmptyValue(value)) {
                    // 不给配置的话，默认会使用series的颜色。这里直接给赋值透明，使point不可见
                    shapeStyle = {
                        fill: 'transparent',
                        stroke: 'transparent',
                    };
                } else {
                    const lineStroke = this.is3D ? fill : border?.stroke;

                    shapeStyle = this.filterShapeAttr({
                        // 如果marker没有颜色，就跟随line的
                        fill: marker.fill || lineStroke,
                        ...marker.border,
                        // 如果marker的border没有颜色，就跟随linestroke的
                        stroke: marker.border?.stroke || lineStroke,
                    });
                }
                return {
                    ...shapeStyle,
                    customStyles: this.getPointCustomStyles(chartDataItem),
                };
            }),
        };
    }
    private getPointCustomStyles(chartDataItem: ChartUIDataItem): CustomStyles {
        const { marker, value } = chartDataItem;
        const { outline, background, ...rest } = filterOriginStyle(chartDataItem);
        let newOutline;
        let newBg;
        if (!marker || isEmptyValue(value)) {
            // 不给配置的话，默认会使用series的颜色。这里直接给赋值透明，使point不可见
            newOutline = { noFill: 1 };
            newBg = { noFill: 1 };
        } else {
            let tempBg;
            let tempLine;
            if (this.is3D) {
                tempBg = background;
                tempLine = applyBgToLineStroke({  }, background);
            } else {
                tempBg = createBgByLineStroke(outline);
                tempLine = tempBg;
                if (tempLine.solidFill) {
                    tempLine.color = tempLine.solidFill.color.srgb_color;
                    delete tempLine.solidFill;
                }
            }
            newBg = marker.background || tempBg;
            newOutline = shallowMerge(tempLine, marker.outline, ['color', 'gradFill', 'noFill']);
        }
        return generateCustomStyles({ outline: newOutline, background: newBg, ...rest });
    }

    private getGeometrySizes() {
        return {
            point: this.createIdMappingCfg<number | undefined>((chartDataItem) => {
                if (!this.hasMarker(chartDataItem)) return 0;
                const { marker, border, serIndex } = chartDataItem;

                const lineWidth = border?.originLineWidth ? border?.lineWidth
                    : this.series[serIndex].border?.lineWidth;

                // 没有size就是"自动"选项，跟随折线宽度
                return marker?.size || lineWidth;
            }),
        };
    }

    private getGeometryShapes() {
        return {
            point: this.createIdMappingCfg<Marker | undefined>((chartDataItem) => {
                const { marker } = chartDataItem;
                return marker?.symbol || DEFAULT_SYMBOL;
            }),
        };
    }
}

class LineChartDataLabel extends DataLabel {
    public static getFiledMappingConfig(data: ChartUIDataItem[], subChart: IBaseSubChartProperty) {
        const config = super.getFiledMappingConfig(data, subChart);
        (config.cfg.layout.cfg as any).minWidth = 250;

        return config;
    }

    protected getLabelPosition(
        value: number,
        dataPointLabel: IDataLabelProperty,
        layout?: IChartManualLayout,
    ): ChartUILabelPosition {
        // 默认靠右
        return labelPositionTypeMap[dataPointLabel.labelPosition || ''] || ChartUILabelPosition.Right;
    }
}
